/**
 * Enterprise Logging System
 * Environment-aware logging with structured output and performance tracking
 */

/* eslint-disable no-console, @typescript-eslint/no-explicit-any */

/* eslint-disable no-unused-vars */
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}
/* eslint-enable no-unused-vars */

export interface LogContext {
  component?: string;
  operation?: string;
  userId?: string;
  duration?: number;
  metadata?: Record<string, any>;
}

class Logger {
  private isDevelopment = import.meta.env['DEV'];
  private logLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
  private performanceEnabled = this.isDevelopment;

  private formatMessage(level: string, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] ${level}:`;

    if (context?.component) {
      return `${prefix} [${context.component}] ${message}`;
    }

    if (context?.operation) {
      return `${prefix} [OP:${context.operation}] ${message}`;
    }

    return `${prefix} ${message}`;
  }

  private logPerformance(context: LogContext): void {
    if (this.performanceEnabled && context.duration !== undefined) {
      const message = `Performance: ${context.operation || 'operation'} took ${context.duration}ms`;
      console.info(this.formatMessage('PERF', message, context));
    }
  }

  error(message: string, error?: any, context?: LogContext): void {
    if (this.logLevel >= LogLevel.ERROR) {
      const formattedMessage = this.formatMessage('ERROR', message, context);

      if (error) {
        console.error(formattedMessage, error);
      } else {
        console.error(formattedMessage);
      }

      // In production, this could integrate with error tracking services like Sentry
      if (!this.isDevelopment && context?.metadata) {
        // Future: Send to error tracking service
      }
    }
  }

  warn(message: string, data?: any, context?: LogContext): void {
    if (this.logLevel >= LogLevel.WARN) {
      const formattedMessage = this.formatMessage('WARN', message, context);

      if (data !== undefined) {
        console.warn(formattedMessage, data);
      } else {
        console.warn(formattedMessage);
      }
    }
  }

  info(message: string, data?: any, context?: LogContext): void {
    if (this.logLevel >= LogLevel.INFO && this.isDevelopment) {
      const formattedMessage = this.formatMessage('INFO', message, context);

      if (data !== undefined) {
        console.info(formattedMessage, data);
      } else {
        console.info(formattedMessage);
      }
    }
  }

  debug(message: string, data?: any, context?: LogContext): void {
    if (this.logLevel >= LogLevel.DEBUG && this.isDevelopment) {
      const formattedMessage = this.formatMessage('DEBUG', message, context);

      if (data !== undefined) {
        console.log(formattedMessage, data);
      } else {
        console.log(formattedMessage);
      }

      if (context) {
        this.logPerformance(context);
      }
    }
  }

  // Utility method for timing operations
  time(label: string): () => void {
    if (!this.performanceEnabled) {
      return () => {};
    }

    const start = performance.now();
    return () => {
      const duration = performance.now() - start;
      this.debug(`${label} completed`, undefined, { operation: label, duration });
    };
  }

  // Create a child logger with additional context
  child(context: LogContext): Logger {
    const childLogger = new Logger();
    // Preserve the context for all child logger calls
    const originalError = childLogger.error.bind(childLogger);
    const originalWarn = childLogger.warn.bind(childLogger);
    const originalInfo = childLogger.info.bind(childLogger);
    const originalDebug = childLogger.debug.bind(childLogger);

    childLogger.error = (message: string, error?: any) => originalError(message, error, context);
    childLogger.warn = (message: string, data?: any) => originalWarn(message, data, context);
    childLogger.info = (message: string, data?: any) => originalInfo(message, data, context);
    childLogger.debug = (message: string, data?: any) => originalDebug(message, data, context);

    return childLogger;
  }
}

// Export singleton instance
export const logger = new Logger();